import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { EMPTY, Observable, Observer, Subject, concatMap, materialize, takeUntil, tap } from 'rxjs';

@Injectable()
export class HttpQueuedInterceptor implements HttpInterceptor, OnDestroy {
  private queue$ = new Subject<{
    request: HttpRequest<unknown>;
    next: HttpHandler;
    observer: Observer<HttpEvent<unknown>>;
    $cancel: Subject<void>;
  }>();

  constructor() {
    this.queue$
      .pipe(
        concatMap(({ request, next, observer, $cancel }) => {
          if ($cancel.closed || $cancel.isStopped) {
            return EMPTY;
          }

          return next.handle(request).pipe(
            materialize(),
            tap({
              next: (notification) => {
                if (notification.kind === 'N') {
                  observer.next(notification.value);
                } else if (notification.kind === 'E') {
                  observer.error(notification.error);
                } else if (notification.kind === 'C') {
                  observer.complete();
                }
              },
              error: (err) => observer.error(err),
              complete: () => observer.complete()
            }),
            takeUntil($cancel)
          );
        })
      )
      .subscribe();
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.headers.has('x-front-queue')) {
      return new Observable<HttpEvent<unknown>>((observer) => {
        let alive = true;
        const $cancel = new Subject<void>();

        queueMicrotask(() => {
          if (alive) {
            this.queue$.next({ request, next, observer, $cancel });
          }
        });

        return () => {
          alive = false;

          $cancel.next();
          $cancel.complete();
        };
      });
    }

    return next.handle(request);
  }

  ngOnDestroy() {
    this.queue$.unsubscribe();
  }
}
